import React, {
	createContext,
	useContext,
	useState,
	ReactNode,
	useRef,
} from 'react';
import { getPatrolData } from 'api/canguard/getPatrolData';
import { getVideosByPatrol, CANCamVideo } from 'api/cancam/cancamApi';
import { PatrolGeneralInfo } from '../../hooks/activePatrolHooks/useGeneralNotesInfo';
import { getCheckpointCompletionStatus } from '../../utils/getCheckpointCompletionStatus';
import { convertToPatrolData } from '../../utils/convertToPatrolData';
import { getPatrolUppyInstance } from '../../utils/getCheckpointUppyInstance';

interface ActivePatrolDataContextType {
	activePatrolID: number | null;
	activePatrolDataRef: React.MutableRefObject<PatrolData | null>;
	patrolAttachments: PatrolAttachment[];
	checklists: PatrolChecklist[];
	generalNotesInfo: PatrolGeneralInfo;
	uppyPatrol: any;
	setActivePatrolID: React.Dispatch<React.SetStateAction<number | null>>;
	fetchAndSetPatrolData: (
		patrol: ScheduleAssignment,
		updateDurationOnSave: boolean,
	) => void;
	handleGeneralStatusChange: (status: PatrolGeneralStatus) => void;
	handleGeneralNotesChange: (text: string) => void;
	handlePatrolAttachmentsUpdate: (attachments: PatrolAttachment[]) => void;
	handleCheckpointInput: (
		checklistID: number,
		checkpointID: number,
		type: CheckpointInputTypes,
		newValue: any,
	) => void;
}

const ActivePatrolDataContext = createContext<
	ActivePatrolDataContextType | undefined
>(undefined);

export const ActivePatrolDataProvider: React.FC<{ children: ReactNode }> = ({
	children,
}) => {
	const [activePatrolID, setActivePatrolID] = useState<number | null>(null); // TODO: Temp fix for state to update in usePatrolCard. This should be removed once the completionStatus part is implemented
	const activePatrolDataRef = useRef<PatrolData | null>(null);
	const [patrolAttachments, setPatrolAttachments] = useState<
		PatrolAttachment[]
	>([]);
	const [generalNotesInfo, setGeneralNotesInfo] = useState<PatrolGeneralInfo>(
		{
			generalNotes: {
				text: '',
				hasError: false,
				helperText: '',
			},
			generalStatus: null,
		},
	);
	const [checklists, setChecklists] = useState<PatrolChecklist[]>([]);
	const [uppyPatrol, setUppyPatrol] = useState<any>(null);

	const handleCheckpointInput = (
		checklistID: number,
		checkpointID: number,
		type: CheckpointInputTypes,
		newValue: any
	  ) => {
		setChecklists((prevChecklists) =>
		  prevChecklists.map((checklist): PatrolChecklist => {
			if (checklist.ChecklistID !== checklistID) return checklist;
	  
			return {
			  ...checklist,
			  Checkpoints: checklist.Checkpoints.map((checkpoint): PatrolCheckpoint => {
				if (checkpoint.CheckpointID !== checkpointID) return checkpoint;
	  
				// 👇 Skip updating Time if DateTime is being updated
				if (type === 'DateTime') {
				  return {
					...checkpoint,
					DateTime: newValue,
					CompletionStatus: getCheckpointCompletionStatus({
					  ...checkpoint,
					  DateTime: newValue,
					}),
				  };
				}
	  
				// 👇 Skip updating DateTime if Time is being updated
				if (type === 'Time') {
				  return {
					...checkpoint,
					Time: newValue,
					CompletionStatus: getCheckpointCompletionStatus({
					  ...checkpoint,
					  Time: newValue,
					}),
				  };
				}
	  
				// General fallback for all other fields
				const updatedCheckpoint = {
				  ...checkpoint,
				  [type]: newValue,
				};
				updatedCheckpoint.CompletionStatus = getCheckpointCompletionStatus(updatedCheckpoint);
	  
				return updatedCheckpoint;
			  }),
			};
		  })
		);
	  };
	  
	  

	const handleGeneralNotesChange = (text: string) => {
		setGeneralNotesInfo((prev) => ({
			...prev,
			generalNotes: {
				...prev.generalNotes,
				text,
			},
		}));
	};

	const handleGeneralStatusChange = (status: PatrolGeneralStatus) => {
		setGeneralNotesInfo((prev) => ({
			...prev,
			generalStatus: status,
		}));
	};

	const fetchAndSetPatrolData = async (
		patrol: ScheduleAssignment,
		updateDurationOnSave: boolean,
	) => {
		const data = await getPatrolData(patrol);
		const patrolData: PatrolData = convertToPatrolData(
			data,
			patrol,
			updateDurationOnSave,
		);
		activePatrolDataRef.current = patrolData;
		setActivePatrolID(patrol.schedule_assignment_id);
		handleGeneralNotesChange(patrolData.comment);
		handleGeneralStatusChange(patrolData.status as PatrolGeneralStatus);
		setChecklists(patrolData.checklists);

		// Start with existing VV/DDS attachments
		let allAttachments = [...patrolData.attachments];

		// Fetch CANcam videos and merge them
		if (patrolData.patrolInfo.comment_list_id) {
			try {
				const cancamVideos = await getVideosByPatrol(patrolData.patrolInfo.comment_list_id);
				const cancamAttachments: PatrolAttachment[] = cancamVideos.map((video: CANCamVideo) => ({
					AttachmentID: video.VideoID,
					Name: video.FileName,
					Size: video.FileSizeBytes || 0,
					ContentType: 'video/mp4',
					Source: 'cancam' as const,
				}));
				allAttachments = [...allAttachments, ...cancamAttachments];
			} catch (error) {
				console.error('[ActivePatrolDataProvider/fetchAndSetPatrolData] Failed to fetch CANcam videos:', error);
				// Continue without CANcam videos if fetch fails
			}
			initializePatrolUppy(patrolData.patrolInfo.comment_list_id);
		} else {
			throw new Error('comment_list_id is not available');
		}

		setPatrolAttachments(allAttachments);
	};

	const initializePatrolUppy = (commentListID: number) => {
		const patrolUppy = getPatrolUppyInstance(commentListID);
		patrolUppy.on('complete', (result: any) => {
			const newAttachments: PatrolAttachment[] = result.successful.map(
				(file: any) => ({
					AttachmentID: file.response.body.VVFileID,
					Name: file.name,
					Size: file.size,
					ContentType: file.type,
				}),
			);
			setPatrolAttachments((prev) => [...prev, ...newAttachments]);
		});
		setUppyPatrol(patrolUppy);
	};

	return (
		<ActivePatrolDataContext.Provider
			value={{
				activePatrolID,
				activePatrolDataRef,
				patrolAttachments,
				checklists,
				generalNotesInfo,
				uppyPatrol,
				setActivePatrolID,
				fetchAndSetPatrolData,
				handleGeneralStatusChange,
				handleGeneralNotesChange,
				handleCheckpointInput,
				handlePatrolAttachmentsUpdate: (
					attachments: PatrolAttachment[],
				) => setPatrolAttachments(attachments),
			}}
		>
			{children}
		</ActivePatrolDataContext.Provider>
	);
};

export const useActivePatrolData = () => {
	const context = useContext(ActivePatrolDataContext);
	if (!context)
		throw new Error(
			'usePatrolData must be used within a PatrolDataProvider',
		);
	return context;
};
